import { useMemo, useState } from 'react';
import { Button, Flex, Popover, Typography } from 'antd';
import {
  AlertFilled,
  BellOutlined,
  CreditCardOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router';
import styled from '@emotion/styled';
import _sortBy from 'lodash/sortBy';

import { useQuery } from '@apollo/client/react';

import useAccountsInfo from 'hooks/useAccountsInfo';
import { getCardDisplayName } from 'utilities/helpers';

import { CUSTOMER_OPPORTUNITIES } from 'queries/customer';
import { useParams } from 'react-router';
import { formatDate } from 'utilities/datesAndTimes';
import { formatDollarsFromCents } from 'utilities/helpers';

const OpportunitiesButton = styled(Button)`
  @keyframes scaleJiggle {
    0%,
    100% {
      transform: scale(1) rotate(0deg);
    }
    10% {
      transform: scale(2) rotate(-15deg);
    }
    20% {
      transform: scale(1.9) rotate(15deg);
    }
    30% {
      transform: scale(2) rotate(-15deg);
    }
    40% {
      transform: scale(1.9) rotate(15deg);
    }
    50% {
      transform: scale(2) rotate(-15deg);
    }
    60% {
      transform: scale(1.9) rotate(15deg);
    }
    70% {
      transform: scale(2) rotate(-15deg);
    }
    80% {
      transform: scale(1.9) rotate(15deg);
    }
    90% {
      transform: scale(2) rotate(-15deg);
    }
  }

  color: var(--neutral-9);
  background-color: var(--neutral-2);

  &.reactive {
    color: var(--orange-2);
    background-color: var(--orange-9);
    &.ant-btn:not(:disabled):not(.ant-btn-disabled):hover {
      color: var(--neutral-0);
      background-color: var(--orange-6);
    }
    &.ant-btn:not(:disabled):not(.ant-btn-disabled):active {
      color: var(--neutral-0);
      background-color: var(--orange-5);
    }
  }

  &.proactive {
    color: var(--green-2);
    background-color: var(--green-9);
    &.ant-btn:not(:disabled):not(.ant-btn-disabled):hover {
      color: var(--neutral-0);
      background-color: var(--green-6);
    }
    &.ant-btn:not(:disabled):not(.ant-btn-disabled):active {
      color: var(--neutral-0);
      background-color: var(--green-5);
    }
  }

  &.animate-jiggle {
    animation: scaleJiggle 1s ease-in-out;
  }
`;

const MOCK_DATA = [
  {
    __typename: 'IncomeUpdateIndicator',
    type: 'INCOME_UPDATE',
    category: 'PROACTIVE',
    scope: 'CUSTOMER',
    accountId: null,
    startedAt: null,
    incomeLastUpdated: '2025-09-14T16:20:31.000Z',
  },
  {
    __typename: 'PaymentDueSoonIndicator',
    type: 'PAYMENT_DUE_SOON',
    category: 'PROACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013791',
    startedAt: null,
    dueDateET: '2026-10-09',
    minimumDue: 2500,
  },
  {
    __typename: 'RecentDeclinesIndicator',
    type: 'RECENT_DECLINES',
    category: 'PROACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013792',
    startedAt: '2026-10-05T13:12:44.000Z',
    count: 2,
    windowHours: 48,
    latest: {
      at: '2026-10-06T14:41:09.000Z',
      merchantName: 'SHELL OIL 57442',
      reason: '51',
      reasonDescription: 'Insufficient funds',
    },
  },
  {
    __typename: 'CardExpiringIndicator',
    type: 'CARD_EXPIRING',
    category: 'PROACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013791',
    startedAt: null,
    cardLast4: '4417',
    expirationDateET: '2026-11-30',
  },
  {
    __typename: 'MulticardEligibleIndicator',
    type: 'MULTICARD_ELIGIBLE',
    category: 'PROACTIVE',
    scope: 'CUSTOMER',
    accountId: null,
    startedAt: null,
  },
  {
    __typename: 'PaymentHoldIndicator',
    type: 'PAYMENT_HOLD',
    category: 'REACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013792',
    startedAt: '2026-10-02T04:00:00.000Z',
    holds: [
      {
        amount: 15000,
        heldSinceET: '2026-10-02',
        creditAvailableByET: '2026-10-10',
      },
      {
        amount: 4000,
        heldSinceET: '2026-10-05',
        creditAvailableByET: '2026-10-13',
      },
    ],
  },
  {
    __typename: 'ReturnedPaymentsIndicator',
    type: 'RETURNED_PAYMENTS',
    category: 'REACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013791',
    startedAt: '2026-09-29T14:03:17.000Z',
    count: 1,
    windowDays: 30,
    latest: {
      returnedAt: '2026-09-29T14:03:17.000Z',
      amount: 5000,
      reasonCode: 'R01',
      reasonDescription: 'Insufficient funds',
    },
  },
  {
    __typename: 'RepeatCallerIndicator',
    type: 'REPEAT_CALLER',
    category: 'REACTIVE',
    scope: 'CUSTOMER',
    accountId: null,
    startedAt: '2026-10-04T18:22:10.000Z',
    contactCount: 3,
    windowHours: 72,
    approximate: true,
  },
  {
    __typename: 'AccountStatusIndicator',
    type: 'ACCOUNT_STATUS',
    category: 'REACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013792',
    startedAt: '2026-08-03T04:00:00.000Z',
    status: 'TIERED_WATCH',
    reasonCode: 'TIERED_WATCH_SUSPECTED_ATO',
  },
  {
    __typename: 'AccountStatusIndicator',
    type: 'ACCOUNT_STATUS',
    category: 'REACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013791',
    startedAt: null,
    status: 'DEBT_SALE_PENDING',
    reasonCode: null,
  },
  {
    __typename: 'PastDueIndicator',
    type: 'PAST_DUE',
    category: 'REACTIVE',
    scope: 'ACCOUNT',
    accountId: '20000013792',
    startedAt: '2026-09-10T04:00:00.000Z',
    amount: 8700,
    daysPastDue: 26,
  },
];

const INDICATOR_TYPE = Object.freeze({
  // REACTIVE
  ACCOUNT_STATUS: 'ACCOUNT_STATUS',
  RECENT_DECLINES: 'RECENT_DECLINES',
  PAYMENT_HOLD: 'PAYMENT_HOLD',
  RETURNED_PAYMENTS: 'RETURNED_PAYMENTS',
  REPEAT_CALLER: 'REPEAT_CALLER',
  PAST_DUE: 'PAST_DUE',

  //PROACTIVE
  INCOME_UPDATE: 'INCOME_UPDATE',
  PAYMENT_DUE_SOON: 'PAYMENT_DUE_SOON',
  CARD_EXPIRING: 'CARD_EXPIRING',
  MULTICARD_ELIGIBLE: 'MULTICARD_ELIGIBLE',
});

const Opportunity = ({ category, title, description, action }) => {
  const color = category === 'REACTIVE' ? 'orange' : 'green';
  return (
    <Flex vertical={true}>
      <Flex gap="var(--spacing-xs)" align="baseline">
        <AlertFilled
          style={{
            fontSize: 'var(--font-size-lg)',
            color: `var(--${color}-5)`,
          }}
        />
        {/* <Typography.Text style={{ color: `var(--${color}-2)` }}> */}
        <Typography.Text>{title}</Typography.Text>
      </Flex>
      <Flex align="baseline">
        <Typography.Text type="secondary" style={{ maxWidth: '500px' }}>
          {description}
        </Typography.Text>
        {action ? (
          <Button
            type="link"
            variant="link"
            size="small"
            onClick={() => {
              action.onClick();
            }}
          >
            {action.text}
          </Button>
        ) : null}
      </Flex>
    </Flex>
  );
};

const OpportunitySection = ({ title, icon, opportunities = [] }) => {
  return (
    <div>
      <Flex
        gap="var(--spacing-xs)"
        style={{
          marginBottom: 'var(--spacing-sm)',
          borderBottom: 'var(--border-secondary)',
        }}
      >
        {icon}
        <Typography.Text strong style={{ fontSize: 'var(--font-size)' }}>
          {title}
        </Typography.Text>
      </Flex>

      <Flex
        vertical={true}
        gap="var(--spacing-sm)"
        style={{
          paddingLeft: 'var(--spacing-sm)',
          marginBottom: 'var(--spacing-sm)',
        }}
      >
        {opportunities.map((opportunity, key) => (
          <Opportunity key={`opportunity-${title}-${key}`} {...opportunity} />
        ))}
      </Flex>
    </div>
  );
};

const OpportunityGroup = ({ title, opportunityGroup = {} }) => {
  const count = opportunityGroup.count || 0;
  const customer = opportunityGroup.customer || [];
  const accounts = opportunityGroup.accounts || [];

  return count ? (
    <Flex
      vertical={true}
      gap="var(--spacing-sm)"
      style={{ fontSize: 'var(--font-size-xs)' }}
    >
      <Typography.Text
        style={{
          fontSize: 'var(--font-size-lg)',
          fontWeight: '600',
        }}
      >
        {title}
      </Typography.Text>

      {customer.length ? (
        <OpportunitySection
          title="Customer"
          icon={<UserOutlined />}
          opportunities={customer}
        />
      ) : null}

      {accounts.length
        ? accounts
            .filter((account) => account.opportunities?.length)
            .map((account) => {
              return account.opportunities.length ? (
                <OpportunitySection
                  key={`account-opportunities-${account.name}`}
                  title={account.name}
                  icon={<CreditCardOutlined />}
                  opportunities={account.opportunities}
                />
              ) : undefined;
            })
        : null}
    </Flex>
  ) : null;
};

const OpportunityPanel = () => {
  const { customerId } = useParams();
  const { accounts, accountsQuery } = useAccountsInfo();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const opportunitiesQuery = useQuery(CUSTOMER_OPPORTUNITIES, {
    variables: { customerId },
  });

  const loading = opportunitiesQuery.loading || accountsQuery.loading;

  const createAction = (text, path) => {
    return {
      text,
      onClick: () => {
        navigate(`/customers/${customerId}${path}`);
        setOpen(false);
      },
    };
  };

  const formatOpportunities = (opportunities) => {
    let count = 0;
    let containsReactive = false;
    const customer = [];
    const accountOpportunitiesById = {};

    accounts.forEach((account) => {
      accountOpportunitiesById[account.cardDetails.id] = {
        name: getCardDisplayName(account),
        last4: account.cardDetails.last4,
        opportunities: [],
      };
    });

    opportunities.forEach((opportunity) => {
      const accountObj = accountOpportunitiesById[opportunity.accountId];
      if (opportunity.accountId && accountObj === undefined) {
        return;
      }

      if (!containsReactive) {
        containsReactive = opportunity.category === 'REACTIVE';
      }

      const opp = {
        category: opportunity.category,
        title: '',
        description: '',
        action: undefined,
      };

      switch (opportunity.type) {
        case INDICATOR_TYPE.INCOME_UPDATE:
          opp.title = 'Income Update';
          opp.description = `Last income update was ${formatDate(opportunity.incomeLastUpdated)}.`;
          opp.action = createAction('Update Income', '/details');
          customer.push(opp);
          count++;
          break;
        case INDICATOR_TYPE.MULTICARD_ELIGIBLE:
          opp.title = 'Multicard';
          opp.description = `Customer is eligible for multicard.`;
          customer.push(opp);
          count++;
          break;
        case INDICATOR_TYPE.REPEAT_CALLER:
          opp.title = 'Repeat Caller';
          opp.description = `Customer has contacted us ${opportunity.approximate ? 'approximately ' : ''}${opportunity.contactCount} times within ${opportunity.windowHours} hours.`;
          customer.push(opp);
          count++;
          break;

        case INDICATOR_TYPE.ACCOUNT_STATUS:
          opp.title = 'Account Status';
          opp.description = `${opportunity.status} ${opportunity.reasonCode ? '- ' + opportunity.reasonCode : ''}`;
          accountObj.opportunities?.push(opp);
          count++;
          break;

        case INDICATOR_TYPE.PAYMENT_DUE_SOON:
          opp.title = 'Payment Due Soon';
          opp.description = `${formatDollarsFromCents(opportunity.minimumDue)} minimum due by ${formatDate(opportunity.dueDateET)}. Remind the customer or offer autopay.`;
          opp.action = createAction(
            'Make Payment',
            `/accounts/credit/${opportunity.accountId}?action=make-payment`,
          );
          accountObj.opportunities?.push(opp);
          count++;
          break;

        case INDICATOR_TYPE.PAST_DUE:
          opp.title = 'Past Due';
          opp.description = `${formatDollarsFromCents(opportunity.amount)} past due, ${opportunity.daysPastDue} days late.`;
          opp.action = createAction(
            'Make Payment',
            `/accounts/credit/${opportunity.accountId}?action=make-payment`,
          );
          accountObj.opportunities?.push(opp);
          count++;
          break;

        case INDICATOR_TYPE.RECENT_DECLINES:
          opp.title = 'Recently Declined Transaction';
          opp.description = `Card ending in ${accountObj.last4} declined on ${formatDate(opportunity.latest?.at)}. (${opportunity.latest?.reasonDescription})`;
          opp.action = createAction(
            'Go to Transactions',
            `/accounts/credit/${opportunity.accountId}/transactions`,
          );
          accountObj.opportunities?.push(opp);
          count++;
          break;

        case INDICATOR_TYPE.RETURNED_PAYMENTS:
          opp.title = 'Returned Payment';
          opp.description = `${formatDollarsFromCents(opportunity.latest?.amount)} payment returned ${formatDate(opportunity.latest?.returnedAt)}. (${opportunity.latest?.reasonDescription})`;
          opp.action = createAction(
            'Go to Transactions',
            `/accounts/credit/${opportunity.accountId}/transactions`,
          );
          accountObj.opportunities?.push(opp);
          count++;
          break;

        case INDICATOR_TYPE.CARD_EXPIRING:
          opp.title = 'Card Expiring';
          opp.description = `Card ending in ${accountObj.last4} expires ${formatDate(opportunity.expirationDateET)}. Confirm the mailing address and that a replacement is on the way.`;
          accountObj.opportunities?.push(opp);
          count++;
          break;

        case INDICATOR_TYPE.PAYMENT_HOLD:
          opp.title = 'Held Payments';
          opp.description = (
            <ul style={{ listStyleType: 'disc', marginBottom: 0 }}>
              {opportunity.holds.map((hold) => {
                return (
                  <li
                    key={`held-payment-${hold.amount}-${hold.creditAvailableByET}`}
                    style={{ marginBottom: 0 }}
                  >
                    {`${formatDollarsFromCents(hold.amount)} releasing on ${formatDate(hold.creditAvailableByET)}`}
                  </li>
                );
              })}
            </ul>
          );
          opp.action = createAction(
            'Go to Transactions',
            `/accounts/credit/${opportunity.accountId}/transactions`,
          );
          accountObj.opportunities?.push(opp);
          count++;
          break;
      }
    });

    return {
      count,
      containsReactive,
      customer,
      accounts: Object.values(accountOpportunitiesById),
    };
  };

  // const [reactiveOpportunities, proactiveOpportunities] = useMemo(() => {
  const formattedOpportunities = useMemo(() => {
    // const indicators = MOCK_DATA;
    const indicators =
      opportunitiesQuery.data?.customerAndAccountIndicators?.indicators || [];
    const reactive = [];
    const proactive = [];

    if (indicators.length) {
      indicators.forEach((indicator) => {
        if (indicator.category === 'REACTIVE') {
          reactive.push(indicator);
        } else if (indicator.category === 'PROACTIVE') {
          proactive.push(indicator);
        }
      });
    }

    // return [formatOpportunities(reactive), formatOpportunities(proactive)];
    return formatOpportunities(_sortBy(indicators, ['category']).reverse());
    // }, [MOCK_DATA, accounts]);
  }, [opportunitiesQuery.data, accounts]);

  const getButtonClasses = () => {
    const hasOpportunities = !loading && formattedOpportunities?.count;

    if (!hasOpportunities) {
      return '';
    }

    const jiggle = !loading ? 'animate-jiggle' : null;
    const state = formattedOpportunities.containsReactive
      ? 'reactive'
      : 'proactive';

    return `${jiggle} ${state}`;
  };

  return (
    <Popover
      open={open}
      onOpenChange={(change) => {
        setOpen(change);
      }}
      placement="rightTop"
      title="Opportunities and Callouts"
      trigger="click"
      styles={{
        content: {
          maxHeight: '600px',
          overflowY: 'scroll',
        },
      }}
      content={() => {
        return formattedOpportunities?.count ? (
          <Flex vertical={true} gap="var(--spacing-sm)">
            {/* <OpportunityGroup
              title="Needs Attention"
              opportunityGroup={reactiveOpportunities}
            />
            <OpportunityGroup
              title="Worth Mentioning"
              opportunityGroup={proactiveOpportunities}
            /> */}
            <OpportunityGroup
              // title="Worth Mentioning"
              opportunityGroup={formattedOpportunities}
            />
          </Flex>
        ) : (
          'Nothing of note'
        );
      }}
    >
      <OpportunitiesButton
        className={getButtonClasses()}
        loading={loading}
        shape="circle"
        size="small"
        variant="solid"
        icon={<BellOutlined />}
      />
    </Popover>
  );
};

export default OpportunityPanel;
